export type EntityType = "goal" | "actor" | "impact" | "feature" | "note" | "feedback" | "deliverable";

export type EntityStatus = "active" | "planned" | "done" | "archived";

export type FrameworkId =
  | "rice"
  | "ice"
  | "value-effort"
  | "weighted-scoring"
  | "wsjf"
  | "cost-of-delay"
  | "moscow"
  | "opportunity-scoring"
  | "eisenhower"
  | "impact-effort"
  | "buy-a-feature"
  | "stack-ranking"
  | "pie-pxl"
  | "story-map"
  | "revenue"
  | "opportunity-solution-tree"
  | "kano"
  | "jobs-to-be-done"
  | "impact-mapping"
  | "experiment"
  | "north-star"
  | "okrs"
  | "gist"
  | "heart"
  | "aarrr"
  | "now-next-later"
  | "three-horizons"
  | "lean-canvas"
  | "value-proposition-canvas"
  | "swot"
  | "five-forces";

export type FieldType =
  | "text"
  | "number"
  | "currency"
  | "percent"
  | "select"
  | "multi-select"
  | "boolean"
  | "computed";

export interface SchemaField {
  key: string;
  label: string;
  type: FieldType;
  readonly?: boolean;
  required?: boolean;
  defaultValue?: unknown;
  options?: string[];
  description?: string;
}

export interface RelationType {
  key: string;
  label: string;
  inverseLabel: string;
}

export interface SyncrelEntity {
  id: string;
  type: EntityType;
  name: string;
  status: EntityStatus;
  fields: Record<string, unknown>;
  doc: string;
  polls?: SpecPoll[];
  position: {
    x: number;
    y: number;
  };
  createdAt: number;
  updatedAt: number;
}

export interface SpecPollOption {
  id: string;
  label: string;
}

export interface SpecPollVote {
  viewerId: string;
  optionId: string;
  votedAt: number;
}

export interface SpecPoll {
  id: string;
  question: string;
  options: SpecPollOption[];
  votes: SpecPollVote[];
  createdAt: number;
  updatedAt: number;
}

export interface SyncrelRelation {
  id: string;
  fromId: string;
  toId: string;
  type: string;
  label: string;
  createdAt: number;
}

export interface RelationPatch {
  type?: string;
  label?: string;
}

export interface SyncrelChatMessage {
  id: string;
  author: string;
  text: string;
  createdAt: number;
}

export interface SyncrelState {
  version: 1;
  workspace: {
    id: string;
    name: string;
    room: string;
    activeFramework: FrameworkId | null;
    enabledFrameworks?: FrameworkId[];
    frameworkWelcomeSeen?: FrameworkId[];
    share?: {
      editKey: string;
      viewKey: string;
    };
    selectedEntityId: string | null;
    selectedRelationId?: string | null;
    updatedAt: number;
  };
  entities: SyncrelEntity[];
  relations: SyncrelRelation[];
  chat?: SyncrelChatMessage[];
  schema: {
    fields: SchemaField[];
    relationTypes: RelationType[];
  };
}

export const cloneState = (state: SyncrelState): SyncrelState => ({
  ...state,
  workspace: { ...state.workspace },
  entities: state.entities.map((entity) => ({
    ...entity,
    fields: { ...entity.fields },
    polls: entity.polls?.map((poll) => ({
      ...poll,
      options: poll.options.map((option) => ({ ...option })),
      votes: poll.votes.map((vote) => ({ ...vote })),
    })),
    position: { ...entity.position },
  })),
  relations: state.relations.map((relation) => ({ ...relation })),
  chat: state.chat?.map((message) => ({ ...message })),
  schema: {
    fields: state.schema.fields.map((field) => ({
      ...field,
      options: field.options ? [...field.options] : undefined,
    })),
    relationTypes: state.schema.relationTypes.map((relationType) => ({ ...relationType })),
  },
});

export const numberField = (fields: Record<string, unknown>, key: string, fallback = 0): number => {
  const value = fields[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

export const stringField = (fields: Record<string, unknown>, key: string, fallback = ""): string => {
  const value = fields[key];
  return typeof value === "string" && value.trim() ? value : fallback;
};
